import { Component,
  EventEmitter,
  OnDestroy,
  OnInit,
  AfterViewInit,
  Output,
  ViewChild,
  ChangeDetectorRef,
  ChangeDetectionStrategy, inject, DestroyRef } from '@angular/core';
import { Subscription } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatSnackBar } from '@angular/material/snack-bar';
import { GlobalService } from '../../../global/services/global.service';
import { WorkspaceService } from '../../../workspace/workspace.service';
import { AppService } from '../../../app.service';
import {
  buildHeaderDataShell,
  generalConfigFromGlobal,
  normalizeFilterParams,
  rowPayloadFromActiveScreen,
  hydratedGlobalParams,
} from '../../../shared/workspace-element-context';
import { RecurringEpService } from '../../services/recurring-ep.service';
import * as _ from 'lodash-es';

@Component({
    selector: 'gtw-recurring-ep',
    templateUrl: './recurring-ep.component.html',
    styleUrls: ['./recurring-ep.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class RecurringEpComponent implements OnInit, AfterViewInit, OnDestroy {
  private readonly destroyRef = inject(DestroyRef);

  private readonly subs = new Subscription();
  @Output('request-grid-update') requestGridUpdate = new EventEmitter<any>();

  // Standard component properties
  currentTabName = "Form Entry";
  minimise = false;
  isSaveClicked = false;

  // Component specific properties
  title = "Recurring E&P Adjustments";
  showEPForm = false;
  crudLoading = false;
  existingLoading = false;
  noRecords = false;
  showFormLoader = false;
  showAdjamtByYear = false;

  // Data properties
  rowData: any = {};
  colData: any = {};
  filterParams: any = {};
  globalService: any = {};
  GENERAL_CONFIG: any = {};
  USER_SETTINGS: any = {};
  SERVER_CONFIG: any = {};
  baseURLs: any = {};

  // Data properties  
  scenario: any;
  taxYear: any;
  mainScreenKey: any;
  LEID: any;
  CDR_NO: any;
  REPORTING_PERIOD: any;
  ME_CODE: any;
  CURR: any;
  COMBINATION_KEY: any;
  line_flag = true;

  // Form data
  editLoadType = 'L';
  parentAcct: any = null;
  offset_acct: any = null;
  parentObject: any = {};
  offsetObject: any = {};
  total_loops_arr: any[] = [];
  total_loops_header_arr: any[] = [];
  existingAdjustments: any[] = [];
  editExistingAdj: any[] = [];
  editHeaderArr: any[] = [];
  
  getLineDetails: any = {
    selected: null,
    list: [],
    disabled: true
  };
  
  getOffsetLineDetails: any = {
    selected: null,
    list: [],
    disabled: true
  };

  isSchdHDisabled = true;
  isOffSetAcctExists = false;
  addAttachment: any = '';

  // Form validation
  @ViewChild('recurringEPadjsForm') recurringEPadjsForm: any;
  formValid = true;
  formErrors: any = {};
  
  // Account search
  parentAccountResults: any[] = [];
  offsetAccountResults: any[] = [];
  showParentResults = false;
  showOffsetResults = false;
  loadingParentAccounts = false;
  loadingOffsetAccounts = false;

  constructor(
    private recurringEpService: RecurringEpService,
    private _snackBar: MatSnackBar,
    private cdr: ChangeDetectorRef,
    private gtwGlobal: GlobalService,
    private gtwWorkspace: WorkspaceService,
    private gtwApp: AppService
  ) { }

  ngOnInit(): void {
    this.applyWorkspaceContext();
    this.subs.add(
      this.gtwWorkspace.setTabActive.subscribe(() => {
        this.applyWorkspaceContext();
        this.init();
      })
    );
    this.subs.add(
      this.gtwGlobal.globalParamsChanged$.subscribe(() => {
        this.applyWorkspaceContext();
        this.init();
      })
    );
    this.init();
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  private applyWorkspaceContext(): void {
    const activeScreen = this.gtwWorkspace.workspaceFactory?.activeScreen || {};
    const header = buildHeaderDataShell(this.gtwGlobal, this.gtwApp);
    header.globalParams = hydratedGlobalParams(this.gtwGlobal, this.gtwWorkspace) || header.globalParams || {};
    this.baseURLs = generalConfigFromGlobal(this.gtwGlobal);
    this.GENERAL_CONFIG = header.generalConfigData || {};
    this.globalService = header;
    this.USER_SETTINGS = header.userSettingsData || {};
    this.SERVER_CONFIG = header.serverConfigData || {};
    this.filterParams = normalizeFilterParams(activeScreen);
    this.rowData = rowPayloadFromActiveScreen(activeScreen);
    this.cdr.markForCheck();
  }

  ngAfterViewInit() {
    // Code combinations are now displayed natively in Angular template
  }

  init() {
    try {
      if (this.rowData && Object.keys(this.rowData).length > 0) {
        this.setupComponentData();
        this.setupFormData();
        this.getAllLineDetails();
        this.getExistingData('LOAD', null);
        // Show form immediately (in AngularJS this was triggered by CCKeySet event)
        this.showEPForm = true;
      }
    } catch (e) {
      console.error('Error in recurring-ep init:', e);
    }
    this.cdr.markForCheck();
  }

  private setupComponentData() {
    // ✅ CRITICAL: MERGE filterParams with existing globalService.globalParams (from headerData)
    // headerData contains GlobalService.globalParams from AngularJS (activity_key, jcd_key, etc.)
    // filterParams contains specific params for this modal (tax_year, scenario, filing_key, parent_me_string)
    // We need BOTH sets of parameters for backend!
    if (!this.globalService || !this.globalService.globalParams) {
      this.globalService = { globalParams: {} };
    }
    
    // Merge: globalParams (from headerData) + filterParams
    this.globalService.globalParams = {
      ...this.globalService.globalParams,  // Keep existing params from headerData
      ...this.filterParams                 // Add/override with filterParams
    };
    
    // Use REAL data from rowData
    this.LEID = this.rowData.LEID;
    this.CDR_NO = this.rowData.CDR_NO || this.rowData.MARS;
    this.REPORTING_PERIOD = this.rowData.REPORTING_PERIOD;
    this.ME_CODE = this.rowData.ME_CODE;
    this.CURR = this.rowData.CURR;
    this.COMBINATION_KEY = this.rowData.COMBINATION_KEY;
    const gp = this.globalService?.globalParams || {};
    this.scenario = this.filterParams.scenario || gp.scenario;
    this.taxYear = this.filterParams.tax_year || gp.tax_year;
    this.mainScreenKey = this.filterParams.screen_key || gp.screen_key;
    
    // Determine line flag based on LE_TAX_TYPE
    if (this.rowData.LE_TAX_TYPE === 'PTSP' || this.rowData.LE_TAX_TYPE === 'BRPTSP' || this.rowData.LE_TAX_TYPE === 'BRPTP') {
      this.line_flag = false;
    }
  }

  private setupFormData() {
    this.crudLoading = false;
    this.existingLoading = false;
    this.noRecords = false;
    this.total_loops_arr = [];
    this.editLoadType = 'L';
    this.existingAdjustments = [];
    this.editExistingAdj = [];
    this.editHeaderArr = [];
    this.isSchdHDisabled = true;
    this.showAdjamtByYear = false;
    
    this.getLineDetails = {
      selected: null,
      list: [],
      disabled: true
    };
    
    this.getOffsetLineDetails = {
      selected: null,
      list: [],
      disabled: true
    };
    
    this.offset_acct = null;
    this.parentAcct = null;
    
    this.parentObject = {
      "LOCAL_ACCT_KEY": "",
      "LOCAL_ACCT": "",
      "SCHD_H_LINE_NO": "0",
      "CHART_KEY": this.rowData.CHART_KEY,
      "ADJ_DESC": "",
      "BEGIN_YEAR": "",
      "END_YEAR": "",
      "APPLY_SAME_ADJ": "N",
      "ADJ_AMOUNT": 0,
      "SUPPL_INFO": "",
      "RECORD_TYPE": 'P',
      "EP_AUTO_KEY": 0,
      "ACCT_DD_DISPLAY": ''
    };
    
    this.offsetObject = {
      "CHART_KEY": this.rowData.CHART_KEY,
      "LOCAL_ACCT_KEY": "",
      "LOCAL_ACCT": "",
      "ACCT_DD_DISPLAY": "",
      "LOCAL_ACCT_TYPE": "",
      "SCHD_H_LINE_NO": "0",
      "ISOFFSETSCHDHDISABLED": true,
      "ADJ_AMT": 0,
      "TRANS_DETAILS_KEY": 0,
      "IS_ACCT_EDITABLE": "Y",
      "ACCT_LINE_NO": "",
      "selectedSchdH": null,
      "RECORD_TYPE": 'O'
    };
  }

  getAllLineDetails() {
    if (!this.GENERAL_CONFIG.base_url) return;
    
    this.recurringEpService.getLineDetails(
      this.GENERAL_CONFIG.base_url,
      this.mainScreenKey,
      this.rowData.LE_TAX_TYPE,
      this.globalService,
      this.filterParams
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (response: any) => {
        if (response.errorMessage && response.errorMessage !== 'null') {
          this._snackBar.open(response.errorMessage, 'Close', {
            duration: 4000,
            panelClass: ['error-snackbar']
          });
        } else if (response.jsonObject) {
          this.getLineDetails.list = response.jsonObject;
          this.getOffsetLineDetails.list = response.jsonObject;
          this.getLineDetails.selected = null;
          
          if (this.getOffsetLineDetails.list.length > 0) {
            this.getOffsetLineDetails.selected = this.getOffsetLineDetails.list[0];
            this.offsetObject["selectedSchdH"] = this.getOffsetLineDetails.list[0];
          }
          
          this.parentObject["SCHD_H_LINE_NO"] = 'N/A';
        }
      },
      (error: any) => {
        console.error('Error loading line details:', error);
        this._snackBar.open('Error loading line details', 'Close', {
          duration: 4000,
          panelClass: ['error-snackbar']
        });
      }
    );
  }

  getExistingData(type: string, epAutoKey: number | null) {
    if (!this.GENERAL_CONFIG.base_url) return;
    
    this.existingLoading = true;
    this.noRecords = false;
    
    this.recurringEpService.getExistingData(
      this.GENERAL_CONFIG.base_url,
      this.COMBINATION_KEY,
      this.rowData.CHART_KEY,
      type,
      epAutoKey,
      this.globalService,
      this.filterParams
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (response: any) => {
        this.existingLoading = false;
        
        if (response.errorMessage && response.errorMessage !== 'null') {
          this._snackBar.open(response.errorMessage, 'Close', {
            duration: 4000,
            panelClass: ['error-snackbar']
          });
        } else if (response.jsonObject) {
          if (type === 'LOAD') {
            this.existingAdjustments = response.jsonObject;
            
            if (this.existingAdjustments.length === 0) {
              this.noRecords = true;
            } else {
              this.noRecords = false;
            }
          } else {
            this.formatAdjData(response.jsonObject);
          }
        } else {
          this.existingAdjustments = [];
          this.noRecords = true;
        }
        this.cdr.markForCheck();
      },
      (error: any) => {
        this.existingLoading = false;
        console.error('Error loading existing data:', error);
        this._snackBar.open('Error loading existing adjustments', 'Close', {
          duration: 4000,
          panelClass: ['error-snackbar']
        });
        this.cdr.markForCheck();
      }
    );
  }

  // Account search with debouncing
  onParentAccountInput(searchString: string) {
    if (!searchString || searchString.length < 3) {
      this.showParentResults = false;
      this.parentAccountResults = [];
      return;
    }

    this.loadingParentAccounts = true;
    this.searchAccounts(searchString, 'PARENT').then(
      (results) => {
        this.parentAccountResults = results;
        this.showParentResults = results.length > 0;
        this.loadingParentAccounts = false;
        this.cdr.detectChanges();
      },
      (error) => {
        console.error('Error searching parent accounts:', error);
        this.loadingParentAccounts = false;
        this.showParentResults = false;
      }
    );
  }

  onOffsetAccountInput(searchString: string) {
    if (!searchString || searchString.length < 3) {
      this.showOffsetResults = false;
      this.offsetAccountResults = [];
      return;
    }

    this.loadingOffsetAccounts = true;
    this.searchAccounts(searchString, 'OFFSET').then(
      (results) => {
        this.offsetAccountResults = results;
        this.showOffsetResults = results.length > 0;
        this.loadingOffsetAccounts = false;
        this.cdr.detectChanges();
      },
      (error) => {
        console.error('Error searching offset accounts:', error);
        this.loadingOffsetAccounts = false;
        this.showOffsetResults = false;
      }
    );
  }

  selectParentAccount(account: any) {
    this.parentAcct = account.ACCT_DD_DISPLAY;
    this.setAccount(account, 'PARENT');
    this.showParentResults = false;
    
    // Clear validation errors after selecting account
    this.checkAcct();
  }

  selectOffsetAccount(account: any) {
    this.offset_acct = account.ACCT_DD_DISPLAY;
    this.setAccount(account, 'OFFSET');
    this.showOffsetResults = false;
    
    // Clear validation errors after selecting account
    this.checkAcct();
  }

  searchAccounts(searchString: string, acctFor: string): Promise<any[]> {
    if (!searchString || searchString.length < 3) {
      return Promise.resolve([]);
    }

    const acctTypeStr = (acctFor === "OFFSET") ? '1,2,3' : '5,6,7';
    const parentAcctKey = (acctFor === 'OFFSET' ? this.parentObject.LOCAL_ACCT_KEY : 0);

    return new Promise((resolve, reject) => {
      this.recurringEpService.getAccounts(
        this.GENERAL_CONFIG.base_url,
        searchString,
        this.mainScreenKey,
        this.parentObject.CHART_KEY,
        parentAcctKey,
        acctTypeStr,
        this.globalService,
        this.filterParams
      ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
        (response: any) => {
          if (response.errorMessage && response.errorMessage !== 'null') {
            reject(response.errorMessage);
          } else if (response.jsonObject) {
            resolve(response.jsonObject);
          } else {
            resolve([]);
          }
        },
        (error: any) => {
          console.error('Account search error:', error);
          reject(error);
        }
      );
    });
  }

  changeSchd() {
    if (this.getLineDetails.selected) {
      this.parentObject["SCHD_H_LINE_NO"] = this.getLineDetails.selected.LINE_NO;
    }
  }

  setAccount(account: any, acctFor: string) {
    const balSheetAccts = [1, 2, 3];
    
    if (acctFor === 'PARENT') {
      this.parentObject["LOCAL_ACCT_KEY"] = account.LOCAL_ACCT_KEY;
      this.parentObject["LOCAL_ACCT"] = account.ACCT;
      this.parentObject["ACCT_DD_DISPLAY"] = account.ACCT_DD_DISPLAY;
      
      if (balSheetAccts.includes(account.TO_ACCT_TYPE)) {
        this.isSchdHDisabled = true;
        if (this.getLineDetails.list.length > 0) {
          this.getLineDetails.selected = this.getLineDetails.list[0];
          this.parentObject["SCHD_H_LINE_NO"] = this.getLineDetails.selected.LINE_NO;
        }
      } else {
        this.isSchdHDisabled = false;
      }
    } else {
      this.offsetObject["LOCAL_ACCT_KEY"] = account.LOCAL_ACCT_KEY;
      this.offsetObject["LOCAL_ACCT"] = account.ACCT;
      this.offsetObject["ACCT_DD_DISPLAY"] = account.ACCT_DD_DISPLAY;
      this.offsetObject["LOCAL_ACCT_TYPE"] = account.LOCAL_ACCT_TYPE;
      this.offsetObject["ACCT_LINE_NO"] = "" + account.LOCAL_ACCT_KEY + this.offsetObject["SCHD_H_LINE_NO"];
      this.isOffSetAcctExists = true;
      
      if (balSheetAccts.includes(account.TO_ACCT_TYPE)) {
        this.offsetObject["ISOFFSETSCHDHDISABLED"] = true;
        if (this.getOffsetLineDetails.list.length > 0) {
          this.offsetObject["selectedSchdH"] = this.getOffsetLineDetails.list[0];
          this.offsetObject["SCHD_H_LINE_NO"] = this.getOffsetLineDetails.selected.LINE_NO;
        }
      } else {
        this.offsetObject["ISOFFSETSCHDHDISABLED"] = false;
      }
    }
  }

  setAdjyear(yearType: string) {
    this.total_loops_header_arr = [
      { "HEADER_NAME": "YEAR", "HEADER_INDEX": "YEAR" },
      { "HEADER_NAME": "Income Statement-" + this.parentObject["LOCAL_ACCT"], "HEADER_INDEX": "CUR_ADJ_AMT" }
    ];
    this.total_loops_arr = [];
    
    const begin_year = parseInt(this.parentObject["BEGIN_YEAR"]);
    const end_year = parseInt(this.parentObject["END_YEAR"]);
    const total_loops = end_year - begin_year + 1;
    
    if (yearType === 'ENDYEAR') {
      this.showAdjamtByYear = true;
      
      if (end_year < begin_year) {
        this.formErrors.endYear = "End Year should be greater than or equal to " + this.taxYear;
        this.formValid = false;
      } else {
        delete this.formErrors.endYear;
        this.formValid = true;
      }
      
      for (let i = 0; i < total_loops; i++) {
        const currentYear = begin_year + i;
        this.total_loops_arr.push({
          YEAR: currentYear,
          CUR_ADJ_AMT: this.parentObject["ADJ_AMOUNT"]
        });
      }
      
      this.setOffsetAdjAmount();
    } else {
      if (begin_year !== parseInt(this.taxYear)) {
        this.formErrors.beginYear = "Begin Year should be " + this.taxYear;
        this.formValid = false;
      } else {
        delete this.formErrors.beginYear;
        this.formValid = true;
      }
    }
  }

  setAdjAmount(type: string) {
    if (this.total_loops_header_arr === undefined) return;
    
    if (type === 'INCOME') {
      this.total_loops_arr.forEach((value, index) => {
        this.total_loops_arr[index]["CUR_ADJ_AMT"] = this.parentObject["ADJ_AMOUNT"];
      });
    } else {
      const nextIndex = this.total_loops_header_arr.length;
      this.total_loops_arr.forEach((value, index) => {
        this.total_loops_arr[index]["OFFSET_AMT_" + nextIndex] = this.offsetObject["ADJ_AMT"];
      });
    }
  }

  setOffsetAdjAmount() {
    this.total_loops_header_arr.splice(0 + 2);
    const nextIndex = this.total_loops_header_arr.length + 1;
    this.total_loops_header_arr.push({
      "HEADER_NAME": "Balance Sheet-" + this.offsetObject["LOCAL_ACCT"],
      "HEADER_INDEX": "OFFSET_AMT_" + nextIndex
    });
    
    const begin_year = parseInt(this.parentObject["BEGIN_YEAR"]);
    const end_year = parseInt(this.parentObject["END_YEAR"]);
    const total_loops = end_year - begin_year + 1;
    
    for (let i = 0; i < total_loops; i++) {
      this.total_loops_arr[i]["OFFSET_AMT_" + nextIndex] = this.offsetObject["ADJ_AMT"];
      this.total_loops_arr[i]["RECORD_TYPE"] = "O";
    }
  }

  checkAcct() {
    // Check parent account (Income Statement Account)
    if (this.parentObject.APPLY_SAME_ADJ === 'Y' && 
        (!this.parentObject.LOCAL_ACCT_KEY || this.parentObject.LOCAL_ACCT_KEY === '')) {
      this.formErrors.account = "Account value is required.";
      this.parentObject.APPLY_SAME_ADJ = 'N';
    } else if (this.parentObject.LOCAL_ACCT_KEY && this.parentObject.LOCAL_ACCT_KEY !== '') {
      // Clear error if account is filled
      delete this.formErrors.account;
    }
    
    // Check offset account (Balance Sheet Account)
    if (!this.offsetObject.LOCAL_ACCT_KEY || this.offsetObject.LOCAL_ACCT_KEY === '') {
      this.formErrors.offsetAccount = "Account value is required.";
    } else {
      // Clear error if account is filled
      delete this.formErrors.offsetAccount;
    }
    
    // Clear fields when checkbox is unchecked
    if (this.parentObject.APPLY_SAME_ADJ === 'N') {
      this.parentObject.ADJ_AMOUNT = 0;
      this.offsetObject.ADJ_AMT = 0;
    }
  }

  onCombinationCodeSet() {
    this.showEPForm = true;
  }

  save() {
    this.checkAcct();
    
    // Simple validation
    if (!this.parentObject.ADJ_DESC) {
      this._snackBar.open('Tax Adj Description is required', 'Close', {
        duration: 4000,
        panelClass: ['error-snackbar']
      });
      return;
    }
    
    if (!this.parentObject.LOCAL_ACCT_KEY) {
      this._snackBar.open('Income Statement Account is required', 'Close', {
        duration: 4000,
        panelClass: ['error-snackbar']
      });
      return;
    }
    
    if (!this.offsetObject.LOCAL_ACCT_KEY) {
      this._snackBar.open('Balance Sheet Account is required', 'Close', {
        duration: 4000,
        panelClass: ['error-snackbar']
      });
      return;
    }
    
    // ✅ CRITICAL: BEGIN_YEAR and END_YEAR validation only for NEW records (editLoadType === 'L')
    // In EDIT mode (editLoadType === 'E'), these fields remain empty strings "" like in AngularJS
    if (this.editLoadType === 'L') {
      if (!this.parentObject.BEGIN_YEAR) {
        this._snackBar.open('Begin Year is required', 'Close', {
          duration: 4000,
          panelClass: ['error-snackbar']
        });
        return;
      }
      
      if (!this.parentObject.END_YEAR) {
        this._snackBar.open('End Year is required', 'Close', {
          duration: 4000,
          panelClass: ['error-snackbar']
        });
        return;
      }
    }
    
    if (!this.formValid) {
      this._snackBar.open('Please correct the errors in the form', 'Close', {
        duration: 4000,
        panelClass: ['error-snackbar']
      });
      return;
    }
    
    this.isSaveClicked = true;
    this.crudLoading = true;
    
    const headerSettings: any[] = [];
    const transactions: any[] = [];
    
    const headerObj: any = {};
    headerObj.tax_year = this.filterParams.tax_year || this.taxYear;
    headerObj.COMBINATION_KEY = this.rowData.COMBINATION_KEY;
    headerObj.TRANS_HEADER_KEY = 0;
    headerObj.EP_AUTO_KEY = this.parentObject["EP_AUTO_KEY"];
    headerObj.ADJ_DESC = this.parentObject["ADJ_DESC"];
    headerObj.APPLY_SAME_ADJ = this.parentObject["APPLY_SAME_ADJ"];
    headerObj.SUPPL_INFO = this.parentObject["SUPPL_INFO"];
    headerObj.BEGIN_YEAR = this.parentObject["BEGIN_YEAR"] || "";  // Empty string in edit mode
    headerObj.END_YEAR = this.parentObject["END_YEAR"] || "";      // Empty string in edit mode
    
    // ✅ CRITICAL: TOTAL_LOOPS calculation differs between NEW and EDIT mode
    if (this.editLoadType === 'E') {
      // In EDIT mode, TOTAL_LOOPS is the length of total_loops_arr (years we have)
      headerObj.TOTAL_LOOPS = this.total_loops_arr.length;
    } else {
      // In NEW mode (L), calculate from BEGIN_YEAR and END_YEAR
      headerObj.TOTAL_LOOPS = parseInt(this.parentObject["END_YEAR"]) - parseInt(this.parentObject["BEGIN_YEAR"]) + 1;
    }
    
    headerSettings.push(headerObj);
    
    // Add parent account transactions
    this.total_loops_arr.forEach((value, index) => {
      const adjModel = this.createAdjustmentModel(
        this.LEID,
        this.CDR_NO,
        this.REPORTING_PERIOD,
        this.ME_CODE,
        this.CURR,
        this.COMBINATION_KEY,
        this.parentObject["LOCAL_ACCT"],
        this.parentObject["LOCAL_ACCT_KEY"],
        this.parentObject["SCHD_H_LINE_NO"],
        this.parentObject["CHART_KEY"],
        this.parentObject["ADJ_AMOUNT"],
        value.YEAR,
        value.CUR_ADJ_AMT,
        "P"
      );
      transactions.push(adjModel);
    });
    
    // Add offset account transactions if exist
    if (this.isOffSetAcctExists) {
      this.total_loops_arr.forEach((value, index) => {
        const offsetIndex = 3;
        
        if (value.hasOwnProperty("OFFSET_AMT_" + offsetIndex)) {
          const adjModel = this.createAdjustmentModel(
            this.LEID,
            this.CDR_NO,
            this.REPORTING_PERIOD,
            this.ME_CODE,
            this.CURR,
            this.COMBINATION_KEY,
            this.offsetObject.LOCAL_ACCT,
            this.offsetObject.LOCAL_ACCT_KEY,
            '0',
            this.offsetObject.CHART_KEY,
            0,
            value.YEAR,
            value["OFFSET_AMT_" + offsetIndex],
            "O"
          );
          transactions.push(adjModel);
        }
      });
    }
    
    // Save adjustments
    this.recurringEpService.saveAdjustments(
      this.GENERAL_CONFIG.base_url,
      headerSettings,
      transactions,
      this.globalService,
      this.filterParams,
      this.rowData
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (response: any) => {
        this.isSaveClicked = false;
        this.crudLoading = false;
        
        if (response.errorMessage && response.errorMessage !== 'null') {
          this._snackBar.open(response.errorMessage, 'Close', {
            duration: 4000,
            panelClass: ['error-snackbar']
          });
        } else {
          this._snackBar.open('Changes have been saved successfully', 'Close', {
            duration: 4000,
            panelClass: ['success-snackbar']
          });
          
          // ✅ CRITICAL: Reload existing adjustments after save to show the new record
          this.reset();
          this.getExistingData('LOAD', null);
          
          this.requestGridUpdate.emit('refresh');
        }
        this.cdr.markForCheck();
      },
      (error: any) => {
        this.isSaveClicked = false;
        this.crudLoading = false;
        this._snackBar.open('Error occurred while saving. Please try again.', 'Close', {
          duration: 4000,
          panelClass: ['error-snackbar']
        });
        this.cdr.markForCheck();
      }
    );
  }

  private createAdjustmentModel(
    leid: any, cdr_no: any, reporting_period: any, me_code: any, curr: any, combination_key: any,
    local_acct: any, local_acct_key: any, schd_h_line_no: any, chart_key: any,
    adj_amount: any, year: any, year_adj_amt: any, record_type: any
  ) {
    return {
      LEID: leid,
      CDR_NO: cdr_no,
      REPORTING_PERIOD: reporting_period,
      ME_CODE: me_code,
      CURR: curr,
      LOCAL_ACCT: local_acct,
      LOCAL_ACCT_KEY: local_acct_key,
      SCHD_H_LINE_NO: schd_h_line_no,
      CHART_KEY: chart_key,
      ADJ_AMOUNT: adj_amount,
      YEAR: year,
      YEAR_ADJ_AMT: year_adj_amt,
      RECORD_TYPE: record_type,
      COMBINATION_KEY: combination_key
    };
  }

  reset() {
    this.setupFormData();
    this.getAllLineDetails();
    this.getExistingData('LOAD', null);
  }

  cancel() {
    this.requestGridUpdate.emit('cancel');
  }

  confirmLoadEPRecurData(index: number) {
    // ✅ Show confirmation dialog (like ng-really-click in AngularJS)
    const message = "Load Recurring EP Adjustment?\n\nAre you sure you want to load this Recurring Adjustment? You have unsaved changes";
    if (window.confirm(message)) {
      this.loadEPRecurData(index);
    }
  }

  loadEPRecurData(index: number) {
    const EP_AUTO_KEY = this.existingAdjustments[index].EP_AUTO_KEY;
    this.getExistingData('EDIT', EP_AUTO_KEY);
    this.editLoadType = 'E';
  }

  confirmDeleteAdjs(adj: any) {
    // ✅ Show confirmation dialog (like ng-really-click in AngularJS)
    const message = "Delete Book Adjustment\n\nAre you sure you want to permanently delete this Recurring Adjustment?";
    if (window.confirm(message)) {
      this.deleteAdjs(adj);
    }
  }

  deleteAdjs(adj: any) {
    this.existingLoading = true;
    
    const returnObj = [{
      "EP_AUTO_KEY": adj.EP_AUTO_KEY,
      "combination_key": this.rowData.COMBINATION_KEY
    }];
    
    this.recurringEpService.deleteAdjustments(
      this.GENERAL_CONFIG.base_url,
      returnObj,
      this.globalService,
      this.filterParams,
      this.rowData
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (response: any) => {
        this.existingLoading = false;
        
        if (response.errorMessage && response.errorMessage !== 'null') {
          this._snackBar.open(response.errorMessage, 'Close', {
            duration: 4000,
            panelClass: ['error-snackbar']
          });
        } else {
          this._snackBar.open('Tax Adjustment has been successfully deleted', 'Close', {
            duration: 4000,
            panelClass: ['success-snackbar']
          });
          
          // ✅ CRITICAL: Reload existing adjustments after delete to refresh the list
          this.getExistingData('LOAD', null);
          
          this.requestGridUpdate.emit('refresh');
        }
        this.cdr.markForCheck();
      },
      (error: any) => {
        this.existingLoading = false;
        this._snackBar.open('Error occurred while deleting. Please try again.', 'Close', {
          duration: 4000,
          panelClass: ['error-snackbar']
        });
        this.cdr.markForCheck();
      }
    );
  }

  formatAdjData(data: any[]) {
    this.total_loops_header_arr = [{ "HEADER_NAME": "Tax Year", "HEADER_INDEX": "YEAR" }];
    this.total_loops_arr = [];
    let nextIndex = 0;
    let lineIndex = 0;
    
    data.forEach((value, key) => {
      if (value.TAXYEAR_RANK === 0 && value.OFFSET_COUNT > 0) {
        this.isOffSetAcctExists = true;
      }
      
      if (value.PARENT_HEADER_VALUE !== 'NA' && value.ROWNUMBER === 1) {
        this.total_loops_header_arr.push({
          "HEADER_NAME": value.PARENT_HEADER_VALUE,
          "HEADER_INDEX": "PARENT"
        });
      }
      
      if (this.isOffSetAcctExists) {
        if (value.OFFSET_HEADER_VALUE !== 'NA') {
          nextIndex = 2 + value.ROWNUMBER;
          this.total_loops_header_arr.push({
            "HEADER_NAME": value.OFFSET_HEADER_VALUE,
            "HEADER_INDEX": "OFFSET_AMT_" + nextIndex
          });
        }
      }
    });
    
    data.forEach((value, key) => {
      if (value.ROWNUMBER === 1) {
        this.total_loops_arr[value.TAXYEAR_RANK] = {};
        nextIndex = 3;
        
        this.total_loops_arr[value.TAXYEAR_RANK]["YEAR"] = value.TAX_YEAR;
        this.total_loops_arr[value.TAXYEAR_RANK]["CUR_ADJ_AMT"] = value.PARENT_AMT;
        this.total_loops_arr[value.TAXYEAR_RANK]["OFFSET_AMT_" + nextIndex] = value.OFFSET_AMT;
        this.parentObject["LOCAL_ACCT"] = value.PARENT_ACCT;
        this.parentObject["ACCT_DD_DISPLAY"] = value.PARENT_ACCT_DD;
        this.parentObject["LOCAL_ACCT_KEY"] = value.PARENT_ACCT_KEY;
        this.parentObject["SCHD_H_LINE_NO"] = value.PARENT_LINE_NO;
        this.parentObject["ADJ_AMOUNT"] = value.PARENT_AMT;
        this.parentObject["ADJ_DESC"] = value.EP_DESC;
        this.parentObject["EP_AUTO_KEY"] = value.EP_AUTO_KEY;
        this.parentObject["SUPPL_INFO"] = value.EP_SUPPL_INFO;
        
        lineIndex = _.findIndex(this.getLineDetails.list, {
          'LINE_NO': this.parentObject.SCHD_H_LINE_NO
        });
        
        if (lineIndex >= 0) {
          this.getLineDetails.selected = this.getLineDetails.list[lineIndex];
        }
        
        if (this.isOffSetAcctExists) {
          this.offsetObject["CHART_KEY"] = this.rowData.CHART_KEY;
          this.offsetObject["LOCAL_ACCT_KEY"] = value.OFFSET_ACCT_KEY;
          this.offsetObject["LOCAL_ACCT"] = value.OFFSET_ACCT;
          this.offsetObject["ACCT_DD_DISPLAY"] = "";
          this.offsetObject["LOCAL_ACCT_TYPE"] = "";
          this.offsetObject["SCHD_H_LINE_NO"] = value.OFFSET_LINE_NO;
          this.offsetObject["ADJ_AMT"] = 0;
          this.offsetObject["TRANS_DETAILS_KEY"] = 0;
          this.offsetObject["IS_ACCT_EDITABLE"] = "Y";
          this.offsetObject["ACCT_LINE_NO"] = "";
          
          if (this.getOffsetLineDetails.list.length > 0) {
            this.offsetObject["selectedSchdH"] = this.getOffsetLineDetails.list[0];
          }
          this.offsetObject["RECORD_TYPE"] = 'O';
        }
      }
      
      if (value.ROWNUMBER > 1) {
        nextIndex = 3 + 1;
        this.total_loops_arr[value.TAXYEAR_RANK]["OFFSET_AMT_" + nextIndex] = value.OFFSET_AMT;
        this.offsetObject["CHART_KEY"] = this.rowData.CHART_KEY;
        this.offsetObject["LOCAL_ACCT_KEY"] = value.OFFSET_ACCT_KEY;
        this.offsetObject["LOCAL_ACCT"] = value.OFFSET_ACCT;
        this.offsetObject["ACCT_DD_DISPLAY"] = "";
        this.offsetObject["LOCAL_ACCT_TYPE"] = "";
        this.offsetObject["SCHD_H_LINE_NO"] = value.OFFSET_LINE_NO;
        this.offsetObject["ADJ_AMT"] = 0;
        this.offsetObject["TRANS_DETAILS_KEY"] = 0;
        this.offsetObject["IS_ACCT_EDITABLE"] = "Y";
        this.offsetObject["ACCT_LINE_NO"] = "";
        
        if (this.getOffsetLineDetails.list.length > 0) {
          this.offsetObject["selectedSchdH"] = this.getOffsetLineDetails.list[0];
        }
        this.offsetObject["RECORD_TYPE"] = 'O';
      }
    });
  }
}

